import React, { useState, useRef, useEffect } from 'react';

const CameraCaptureModal = ({ isOpen, onClose, onCapture }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [stream, setStream] = useState(null);
    const [capturedImage, setCapturedImage] = useState(null);
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState('');
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            getDevices();
            startCamera();
        } else {
            stopCamera();
        }
        return () => stopCamera();
    }, [isOpen]);

    useEffect(() => {
        if (selectedDeviceId && isOpen) {
            startCamera(selectedDeviceId);
        }
    }, [selectedDeviceId]);

    const getDevices = async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === 'videoinput');
            setDevices(videoDevices);
            if (videoDevices.length > 0 && !selectedDeviceId) {
                setSelectedDeviceId(videoDevices[0].deviceId);
            }
        } catch (err) {
            console.error("Error enumerating devices:", err);
        }
    };

    const startCamera = async (deviceId = null) => {
        stopCamera();
        setError(null);
        try {
            const constraints = {
                video: deviceId ? { deviceId: { exact: deviceId } } : true
            };
            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
            setStream(mediaStream);
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
            }
        } catch (err) {
            console.error("Error accessing camera:", err);
            setError("No se pudo acceder a la cámara. Verifique los permisos.");
        }
    };

    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
    };

    const handleCapture = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            setCapturedImage(canvas.toDataURL('image/png'));
        }
    };

    const handleRetake = () => {
        setCapturedImage(null);
    };

    const handleConfirm = () => {
        if (capturedImage) {
            onCapture(capturedImage);
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 font-sans">
            <div className="bg-slate-900 rounded-2xl border border-slate-700 p-6 max-w-3xl w-full shadow-2xl flex flex-col">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <span className="text-2xl">📸</span>
                        <h3 className="text-xl font-bold text-white">Captura de Cámara</h3>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>

                <div className="flex-1 bg-black rounded-xl overflow-hidden relative aspect-video mb-4 border border-slate-700">
                    {error ? (
                        <div className="absolute inset-0 flex items-center justify-center text-red-400">
                            {error}
                        </div>
                    ) : capturedImage ? (
                        <img src={capturedImage} alt="Captured" className="w-full h-full object-contain" />
                    ) : (
                        <video ref={videoRef} autoPlay playsInline className="w-full h-full object-contain transform scale-x-[-1]" />
                    )}
                    <canvas ref={canvasRef} className="hidden" />
                </div>

                <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                        {!capturedImage && devices.length > 1 && (
                            <select
                                value={selectedDeviceId}
                                onChange={(e) => setSelectedDeviceId(e.target.value)}
                                className="bg-slate-800 text-white text-sm border border-slate-600 rounded px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500 w-full max-w-xs"
                            >
                                {devices.map(device => (
                                    <option key={device.deviceId} value={device.deviceId}>
                                        {device.label || `Cámara ${devices.indexOf(device) + 1}`}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    <div className="flex space-x-3">
                        {capturedImage ? (
                            <>
                                <button onClick={handleRetake} className="px-6 py-2 text-slate-300 border border-slate-600 rounded-lg hover:bg-slate-800 transition-colors">
                                    Reintentar
                                </button>
                                <button onClick={handleConfirm} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-bold shadow-lg shadow-blue-900/50">
                                    Usar Foto
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={handleCapture}
                                disabled={!!error}
                                className="px-8 py-3 bg-white text-slate-900 rounded-full hover:bg-slate-200 transition-all font-bold flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <div className="w-4 h-4 rounded-full bg-red-500 animate-pulse"></div>
                                <span>Capturar</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CameraCaptureModal;
